import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, Loader2, AlertTriangle, CalendarX, ClipboardCheck } from "lucide-react";
import { CRM_API, errMsg } from "./api";
import { api as PUBLIC_API } from "../lib/api";
import { SearchSelect, NewCustomerForm, NewVehicleForm } from "./components/QuickPick";
import PageHeader from "./components/PageHeader";
import { useNavigate } from "react-router-dom";

const inp = "w-full bg-white/5 border border-white/12 rounded-lg px-3 py-2 outline-none focus:border-[#1F5EFF] text-white text-sm";

const STATUS_COLORS = {
  booked: "bg-white/15 text-white/80",
  confirmed: "bg-blue-500/20 text-blue-300",
  arrived: "bg-purple-500/20 text-purple-300",
  checked_in: "bg-[#1F5EFF]/25 text-[#8db2ff]",
  in_progress: "bg-amber-500/20 text-amber-300",
  waiting_customer: "bg-orange-500/20 text-orange-300",
  quality_control: "bg-pink-500/20 text-pink-300",
  ready_for_pickup: "bg-emerald-500/20 text-emerald-300",
  completed: "bg-emerald-600/30 text-emerald-200",
  cancelled: "bg-red-500/20 text-red-300",
  no_show: "bg-red-700/20 text-red-400",
};

const STATUS_LIST = Object.keys(STATUS_COLORS);

// Naive Edmonton-local ISO ("YYYY-MM-DDTHH:mm:00") — the calendar, day filters
// and customer emails all treat scheduled_at as shop-local wall time.
const pad2 = (n) => String(n).padStart(2, "0");
const localNaive = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;

const HOUR_START = 8;
const HOUR_END = 20; // 8 AM → 8 PM

function isoDate(d) { return d.toISOString().slice(0, 10); }
function startOfWeek(d) {
  const day = d.getDay() || 7;
  const s = new Date(d);
  s.setDate(d.getDate() - (day - 1));
  s.setHours(0, 0, 0, 0);
  return s;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function Calendar() {
  const [mode, setMode] = useState("week"); // day | week | month
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [bays, setBays] = useState([]);
  const [modal, setModal] = useState(null); // { slot } or { appt }
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockedDays, setBlockedDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const days = useMemo(() => {
    if (mode === "day") return [cursor];
    const s = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => addDays(s, i));
  }, [cursor, mode]);

  const load = async () => {
    setLoading(true);
    try {
      let from, to;
      if (mode === "month") {
        const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const gridStart = startOfWeek(first);
        from = isoDate(gridStart);
        to = isoDate(addDays(gridStart, 42));
      } else {
        from = isoDate(days[0]);
        to = isoDate(addDays(days[days.length - 1], 1));
      }
      const [a, b, bl] = await Promise.all([
        CRM_API.get(`/appointments?date_from=${from}T00:00:00&date_to=${to}T00:00:00`),
        CRM_API.get("/bays"),
        CRM_API.get("/calendar/blocks"),
      ]);
      setAppts(a.data); setBays(b.data.filter((x) => x.active));
      setBlockedDays(bl.data.blocked_days || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor, mode]);

  const nextPeriod = () => setCursor(mode === "day" ? addDays(cursor, 1) : mode === "week" ? addDays(cursor, 7) : new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const prevPeriod = () => setCursor(mode === "day" ? addDays(cursor, -1) : mode === "week" ? addDays(cursor, -7) : new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));

  const apptsForDay = (day) => appts.filter((a) => a.scheduled_at?.startsWith(isoDate(day)));

  const rangeLabel = mode === "day"
    ? cursor.toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" })
    : mode === "month"
      ? cursor.toLocaleDateString("en-CA", { month: "long", year: "numeric" })
      : `${days[0].toLocaleDateString("en-CA", { month: "short", day: "numeric" })} — ${days[6].toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div data-testid="crm-calendar">
      <PageHeader
        eyebrow="Studio schedule"
        title="Calendar"
        subtitle={rangeLabel}
        right={
          <div className="flex items-center gap-2">
            <div className="flex bg-white/5 border border-white/10 rounded-lg p-0.5">
              {["day", "week", "month"].map((m) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`cal-mode-${m}`}
                  className={`px-3 py-1.5 text-xs uppercase font-mono-alt tracking-widest rounded-md transition-colors ${mode === m ? "bg-[#1F5EFF] text-white" : "text-white/50 hover:text-white"}`}>
                  {m}
                </button>
              ))}
            </div>
            <button onClick={prevPeriod} className="p-2 rounded-lg hover:bg-white/5 text-white/70" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setCursor(new Date())} className="px-3 py-1.5 text-xs rounded-lg border border-white/15 hover:bg-white/5 text-white/70" data-testid="cal-today">Today</button>
            <button onClick={nextPeriod} className="p-2 rounded-lg hover:bg-white/5 text-white/70" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={() => setBlockOpen(true)} data-testid="cal-block-day"
              className="ml-2 px-4 py-2 rounded-lg border border-red-400/30 text-red-300 hover:bg-red-500/10 text-sm font-medium">
              <CalendarX className="w-4 h-4 inline mr-1" />Block day
            </button>
            <button onClick={() => setModal({ slot: { start: localNaive(cursor), duration: 60 } })} data-testid="cal-new-appt"
              className="px-4 py-2 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-sm font-medium">
              <Plus className="w-4 h-4 inline mr-1" />New
            </button>
          </div>
        }
      />

      <div className="p-6 md:p-8">
        {loading && <div className="text-white/40 text-sm mb-3"><Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" /> Loading…</div>}

        {mode === "month" ? (
          <MonthGrid cursor={cursor} appts={appts} blockedDays={blockedDays}
            onOpenDay={(d) => { setCursor(d); setMode("day"); }} />
        ) : (
        <>
        {/* Grid header */}
        <div className={`grid gap-2 mb-2`} style={{ gridTemplateColumns: `70px repeat(${days.length}, minmax(140px, 1fr))` }}>
          <div />
          {days.map((d) => {
            const today = isoDate(d) === isoDate(new Date());
            const blocked = blockedDays.includes(isoDate(d));
            return (
              <div key={d.toISOString()} className={`text-center py-2 rounded-lg ${blocked ? "bg-red-500/10 border border-red-400/40" : today ? "bg-[#1F5EFF]/15 border border-[#1F5EFF]/40" : ""}`} data-testid={`day-header-${isoDate(d)}`}>
                <div className="font-mono-alt text-[9px] uppercase tracking-widest text-white/40">{d.toLocaleDateString("en-CA", { weekday: "short" })}</div>
                <div className={`font-display text-lg ${blocked ? "text-red-300" : today ? "text-[#8db2ff]" : "text-white/80"}`}>{d.getDate()}</div>
                {blocked && <div className="font-mono-alt text-[8px] uppercase tracking-widest text-red-300/80" data-testid={`day-blocked-${isoDate(d)}`}>Blocked</div>}
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <div className={`relative grid gap-2`} style={{ gridTemplateColumns: `70px repeat(${days.length}, minmax(140px, 1fr))` }}>
          {/* Hour labels */}
          <div className="flex flex-col">
            {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => (
              <div key={i} className="h-16 text-right pr-2 text-[10px] font-mono-alt text-white/30 uppercase tracking-widest border-b border-white/5">
                {`${(HOUR_START + i).toString().padStart(2, "0")}:00`}
              </div>
            ))}
          </div>

          {days.map((day) => {
            const dayAppts = apptsForDay(day);
            return (
              <div key={day.toISOString()} className="relative bg-white/2 border border-white/8 rounded-lg overflow-hidden" data-testid={`day-column-${isoDate(day)}`}>
                {/* Hour cells (clickable to create) */}
                {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => {
                  const hour = HOUR_START + i;
                  return (
                    <button key={i}
                      onClick={() => {
                        const s = new Date(day); s.setHours(hour, 0, 0, 0);
                        setModal({ slot: { start: localNaive(s), duration: 60 } });
                      }}
                      className="block w-full h-16 border-b border-white/5 hover:bg-[#1F5EFF]/10 transition-colors"
                    />
                  );
                })}
                {/* Appointments overlayed */}
                {dayAppts.map((a) => {
                  const start = new Date(a.scheduled_at);
                  const startMin = start.getHours() * 60 + start.getMinutes() - HOUR_START * 60;
                  const dur = a.duration_min || 60;
                  const top = (startMin / 60) * 4; // 4rem = 64px per hour
                  const height = Math.max(2, (dur / 60) * 4);
                  return (
                    <button key={a.id}
                      onClick={() => setModal({ appt: a })}
                      data-testid={`appt-${a.id}`}
                      className={`absolute left-1 right-1 rounded-md px-2 py-1 text-left border transition-transform hover:scale-[1.02] overflow-hidden ${STATUS_COLORS[a.status] || "bg-white/10 text-white"}`}
                      style={{ top: `${top}rem`, height: `${height}rem`, borderColor: a.bay_color || "rgba(255,255,255,0.15)" }}
                    >
                      <div className="text-xs font-medium truncate leading-tight">{a.customer_name || "—"}</div>
                      <div className="text-[10px] opacity-80 truncate">{a.vehicle_summary || "—"}</div>
                      <div className="text-[9px] font-mono-alt opacity-60 uppercase mt-0.5">
                        {start.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", hour12: false })} · {a.bay_name || "No bay"}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        </>
        )}
      </div>

      {modal && (
        <AppointmentModal
          slot={modal.slot} appt={modal.appt} bays={bays}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
          onOpenWO={() => { if (modal.appt) nav(`/crm/appointments/${modal.appt.id}`); }}
          onCheckIn={() => { if (modal.appt) nav(`/crm/check-in?customer=${modal.appt.customer_id}&vehicle=${modal.appt.vehicle_id || ""}`); }}
        />
      )}
      {blockOpen && (
        <BlockDayModal
          blockedDays={blockedDays}
          onClose={() => setBlockOpen(false)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}

// Month overview: 6×7 grid, appointment chips per day, click a day to open it.
function MonthGrid({ cursor, appts, blockedDays, onOpenDay }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const todayIso = isoDate(new Date());

  return (
    <div data-testid="calendar-month-grid">
      <div className="grid grid-cols-7 gap-1.5 mb-1.5">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="text-center font-mono-alt text-[9px] uppercase tracking-widest text-white/35 py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d) => {
          const iso = isoDate(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const blocked = blockedDays.includes(iso);
          const dayAppts = appts
            .filter((a) => a.scheduled_at?.startsWith(iso))
            .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
          return (
            <button
              key={iso}
              onClick={() => onOpenDay(d)}
              data-testid={`month-day-${iso}`}
              className={`min-h-24 rounded-lg border p-1.5 text-left align-top transition-colors flex flex-col ${
                blocked
                  ? "border-red-400/40 bg-red-500/8 hover:bg-red-500/15"
                  : iso === todayIso
                    ? "border-[#1F5EFF]/50 bg-[#1F5EFF]/8 hover:bg-[#1F5EFF]/15"
                    : "border-white/8 bg-white/2 hover:bg-white/5"
              } ${inMonth ? "" : "opacity-35"}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-medium ${blocked ? "text-red-300" : iso === todayIso ? "text-[#8db2ff]" : "text-white/70"}`}>
                  {d.getDate()}
                </span>
                {blocked && <span className="font-mono-alt text-[7px] uppercase tracking-widest text-red-300/80" data-testid={`month-blocked-${iso}`}>Blocked</span>}
              </div>
              <div className="space-y-0.5 overflow-hidden flex-1">
                {dayAppts.slice(0, 3).map((a) => (
                  <div key={a.id}
                    className={`truncate rounded px-1 py-0.5 text-[9px] leading-tight border ${STATUS_COLORS[a.status] || "bg-white/10 text-white"}`}
                    style={{ borderColor: a.bay_color || "rgba(255,255,255,0.12)" }}>
                    {a.scheduled_at.slice(11, 16)} {a.customer_name || "—"}
                  </div>
                ))}
                {dayAppts.length > 3 && (
                  <div className="text-[9px] text-white/40 px-1">+{dayAppts.length - 3} more</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BlockDayModal({ blockedDays, onClose, onChanged }) {  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const block = async () => {
    if (!date) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      const { data } = await CRM_API.post("/calendar/block", { date, reason: reason || undefined });
      toast.success(`Blocked ${data.date} on ${data.bays_blocked} bay(s)` +
        (data.already_blocked ? ` (${data.already_blocked} already blocked)` : ""));
      setDate(""); setReason("");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const unblock = async (d) => {
    if (!confirm(`Unblock ${d}? Online booking will reopen for this day.`)) return;
    try {
      const { data } = await CRM_API.post("/calendar/unblock", { date: d });
      toast.success(`Unblocked ${d} (${data.removed} slot blockers removed)`);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-[#131319] border border-white/12 rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="block-day-modal">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <CalendarX className="w-5 h-5 text-red-300" />
          <div className="flex-1 text-white font-display text-lg">Block a day</div>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white" data-testid="block-day-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-white/50 text-xs leading-relaxed">
            Blocked days show zero availability for online booking (all bays are marked busy
            for the whole day). No emails or reminders are sent for these blocks.
          </p>
          <label className="block">
            <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">DATE</div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inp} data-testid="block-day-date" />
          </label>
          <label className="block">
            <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">REASON (OPTIONAL)</div>
            <input value={reason} onChange={(e) => setReason(e.target.value)} className={inp} placeholder="e.g. Private event, staff training" data-testid="block-day-reason" />
          </label>
          <button onClick={block} disabled={busy} data-testid="block-day-submit"
            className="w-full px-4 py-2.5 rounded-lg bg-red-500/80 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-40">
            {busy ? "Blocking…" : "Block this day"}
          </button>

          {blockedDays.length > 0 && (
            <div>
              <div className="text-[11px] font-mono-alt text-white/50 mb-2 tracking-widest">CURRENTLY BLOCKED</div>
              <div className="space-y-1.5" data-testid="blocked-days-list">
                {blockedDays.map((d) => (
                  <div key={d} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/3 border border-white/10" data-testid={`blocked-day-${d}`}>
                    <span className="text-sm text-white/80">{new Date(d + "T12:00:00").toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>
                    <button onClick={() => unblock(d)} className="text-xs text-red-300 hover:text-red-200" data-testid={`unblock-${d}`}>Unblock</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AppointmentModal({ slot, appt, bays, prefill, onClose, onSaved, onCheckIn }) {
  const isEdit = !!appt;
  const [services, setServices] = useState([]);
  const [baysList, setBaysList] = useState(bays || []);
  const [selCustomer, setSelCustomer] = useState(null);
  const [selVehicle, setSelVehicle] = useState(null);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [form, setForm] = useState({
    customer_id: appt?.customer_id || prefill?.customer_id || "",
    vehicle_id: appt?.vehicle_id || prefill?.vehicle_id || "",
    service_ids: appt?.service_ids || [],
    scheduled_at: appt?.scheduled_at || prefill?.scheduled_at || slot?.start || localNaive(new Date()),
    duration_min: appt?.duration_min || prefill?.duration_min || 60,
    bay_id: appt?.bay_id || "",
    employee_id: appt?.employee_id || "",
    estimated_price: appt?.estimated_price ?? prefill?.estimated_price ?? 0,
    notes: appt?.notes ?? prefill?.notes ?? "",
  });
  const [conflict, setConflict] = useState(null);
  const [busy, setBusy] = useState(false);
  const [askDecision, setAskDecision] = useState(null);
  const [sendConfirm, setSendConfirm] = useState(true);
  const [promo, setPromo] = useState(null);
  const [pricingCfg, setPricingCfg] = useState(null);
  const [vclass, setVclass] = useState(null);

  // Classify the picked vehicle -> website D1–D5 class pricing
  useEffect(() => {
    setVclass(null);
    if (!selVehicle?.year || !selVehicle?.make || !selVehicle?.model) return;
    PUBLIC_API.post("/vehicles/classify", { year: Number(selVehicle.year), make: selVehicle.make, model: selVehicle.model })
      .then(({ data }) => setVclass(data?.vehicle_class || null))
      .catch(() => {});
  }, [selVehicle]);

  // Website-catalog price for a synced CRM service (class price when known)
  const svcWebPrice = (s) => {
    if (s.pricing_key && pricingCfg) {
      const ps = (pricingCfg.services || []).find((x) => x.key === s.pricing_key);
      if (ps) {
        if (vclass && ps.prices?.[vclass] != null) return ps.prices[vclass];
        if (ps.options?.length) return Math.min(...ps.options.map((o) => o.price));
        if (ps.flat_price != null) return ps.flat_price;
        const vals = Object.values(ps.prices || {}).filter((v) => v != null);
        if (vals.length) return Math.min(...vals);
      }
    }
    return Number(s.price) || 0;
  };

  // Grand Opening math for manual bookings: 50% off the most expensive
  // selected service, 20% off every other — mirrors the website promo.
  const promoQuote = useMemo(() => {
    if (!promo?.active) return null;
    if (promo.expires_at && new Date(`${promo.expires_at}T23:59:59`) < new Date()) return null;
    const sel = services
      .filter((s) => form.service_ids.includes(s.id))
      .map((s) => ({ name: s.name, price: svcWebPrice(s), pkey: s.pricing_key }))
      .filter((s) => s.price > 0)
      .sort((a, b) => b.price - a.price);
    if (!sel.length) return null;
    const main = Number(promo.percent) || 50;
    const add = Number(promo.multi_percent) || 20;
    const svcPcts = promo.service_percents || {};
    const lines = sel.map((s, i) => {
      const pct = Number(svcPcts[s.pkey]) || (i === 0 ? main : add);
      return { ...s, pct, final: Math.floor(s.price * (100 - pct) / 100) };
    });
    return {
      total: lines.reduce((t, l) => t + l.final, 0),
      regular: lines.reduce((t, l) => t + l.price, 0),
      lines,
    };
  }, [promo, services, form.service_ids, pricingCfg, vclass]);

  useEffect(() => {
    if (!bays) CRM_API.get("/bays").then(({ data }) => setBaysList(data)).catch(() => {});
    PUBLIC_API.get("/pricing/config").then(({ data }) => { setPromo(data?.promo || null); setPricingCfg(data); }).catch(() => {});
    CRM_API.get("/services?active_only=true").then(({ data }) => {
      setServices(data);
      // Quote-conversion prefill: match requested service names to CRM catalog
      if (prefill?.service_names?.length) {
        const match = (name) => {
          const n = String(name).toLowerCase().trim();
          return data.find((s) => s.name.toLowerCase() === n)
              || data.find((s) => n.includes(s.name.toLowerCase()) || s.name.toLowerCase().includes(n));
        };
        const ids = prefill.service_names.map(match).filter(Boolean).map((s) => s.id);
        if (ids.length) setForm((f) => ({ ...f, service_ids: [...new Set([...f.service_ids, ...ids])] }));
      }
    });
    const cid = appt?.customer_id || prefill?.customer_id;
    const vid = appt?.vehicle_id || prefill?.vehicle_id;
    if (cid) CRM_API.get(`/customers/${cid}`).then(({ data }) => setSelCustomer(data.customer)).catch(() => {});
    if (vid) CRM_API.get(`/vehicles/${vid}`).then(({ data }) => setSelVehicle(data.vehicle)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!form.customer_id) { toast.error("Select customer"); return; }
    setBusy(true);
    try {
      const payload = { ...form, duration_min: Number(form.duration_min), estimated_price: Number(form.estimated_price) || 0,
        send_confirmation: !isEdit && sendConfirm && !!selCustomer?.email };
      const { data } = isEdit
        ? await CRM_API.patch(`/appointments/${appt.id}`, payload)
        : await CRM_API.post("/appointments", payload);
      if (data.conflict) { setConflict(data.conflict); toast.warning("Bay conflict — saved anyway"); }
      else toast.success(isEdit ? "Updated" : "Created");
      if (!isEdit && payload.send_confirmation) {
        const ce = data.confirmation_email;
        if (ce?.ok) toast.success(`Confirmation emailed to ${selCustomer.email}`);
        else if (ce?.skipped) toast.info(`Confirmation prepared (email skipped: ${ce.reason})`);
        else if (ce?.no_email) toast.warning("Customer has no email — confirmation not sent");
        else if (ce && !ce.ok) toast.error("Confirmation email failed to send");
      }
      onSaved(data?.id);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    try { await CRM_API.patch(`/appointments/${appt.id}/status`, { status }); toast.success("Status updated"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const decide = async () => {
    setBusy(true);
    try {
      await CRM_API.patch(`/appointments/${appt.id}/status`, { status: askDecision });
      toast.success(askDecision === "confirmed"
        ? "Confirmed — customer notified by email"
        : "Declined — customer notified by email");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Delete this appointment?")) return;
    try { await CRM_API.delete(`/appointments/${appt.id}`); toast.success("Deleted"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const dtLocal = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl bg-[#131319] border border-white/12 rounded-2xl overflow-hidden my-8" onClick={(e) => e.stopPropagation()} data-testid="appt-modal">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <div className="flex-1 text-white font-display text-lg">{isEdit ? `Appointment ${new Date(appt.scheduled_at).toLocaleString("en-CA")}` : "New appointment"}</div>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {conflict && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              Conflict with another appointment in this bay at that time.
            </div>
          )}
          <div>
            <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">CUSTOMER</div>
            {addingCustomer ? (
              <NewCustomerForm
                onCreated={(c) => {
                  setSelCustomer(c);
                  setSelVehicle(null);
                  setForm({ ...form, customer_id: c.id, vehicle_id: "" });
                  setAddingCustomer(false);
                }}
                onCancel={() => setAddingCustomer(false)}
              />
            ) : (
              <SearchSelect
                testId="appt-customer"
                placeholder="Search by name, phone or email…"
                addLabel="Add new customer"
                fetchResults={async (q) => (await CRM_API.get(`/customers?q=${encodeURIComponent(q)}&limit=20`)).data}
                renderItem={(c, pick) => (
                  <button type="button" key={c.id} onClick={() => pick(c)} data-testid={`appt-customer-option-${c.id}`}
                    className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/5">
                    {c.first_name} {c.last_name} <span className="text-white/40">· {c.phone}</span>
                  </button>
                )}
                onSelect={(c) => { setSelCustomer(c); setSelVehicle(null); setForm({ ...form, customer_id: c.id, vehicle_id: "" }); }}
                onAddNew={() => setAddingCustomer(true)}
                selectedLabel={selCustomer ? `${selCustomer.first_name || ""} ${selCustomer.last_name || ""} · ${selCustomer.phone || ""}`.trim() : ""}
                onClear={() => { setSelCustomer(null); setSelVehicle(null); setForm({ ...form, customer_id: "", vehicle_id: "" }); }}
              />
            )}
          </div>
          <div>
            <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">VEHICLE (OPTIONAL)</div>
            {addingVehicle ? (
              <NewVehicleForm
                customerId={form.customer_id}
                onCreated={(v) => { setSelVehicle(v); setForm({ ...form, vehicle_id: v.id }); setAddingVehicle(false); }}
                onCancel={() => setAddingVehicle(false)}
              />
            ) : (
              <SearchSelect
                testId="appt-vehicle"
                placeholder={form.customer_id ? "Search this customer's vehicles…" : "Select a customer first"}
                disabled={!form.customer_id}
                addLabel="Add new vehicle"
                fetchResults={async (q) => (await CRM_API.get(`/vehicles?customer_id=${form.customer_id}&q=${encodeURIComponent(q)}&limit=20`)).data}
                renderItem={(v, pick) => (
                  <button type="button" key={v.id} onClick={() => pick(v)} data-testid={`appt-vehicle-option-${v.id}`}
                    className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/5">
                    {[v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"} <span className="text-white/40">· {v.license_plate || v.vin?.slice(-6) || "no plate"}</span>
                  </button>
                )}
                onSelect={(v) => { setSelVehicle(v); setForm({ ...form, vehicle_id: v.id }); }}
                onAddNew={() => setAddingVehicle(true)}
                selectedLabel={selVehicle ? `${[selVehicle.year, selVehicle.make, selVehicle.model].filter(Boolean).join(" ") || "Vehicle"} · ${selVehicle.license_plate || selVehicle.vin?.slice(-6) || "no plate"}` : ""}
                onClear={() => { setSelVehicle(null); setForm({ ...form, vehicle_id: "" }); }}
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">WHEN</div>
              <input type="datetime-local" value={dtLocal(form.scheduled_at)} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value.length === 16 ? e.target.value + ":00" : e.target.value })} className={inp} data-testid="appt-when" />
            </label>
            <label className="block">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">DURATION (MIN)</div>
              <input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className={inp} min="15" step="15" data-testid="appt-duration" />
            </label>
            <label className="block">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">BAY</div>
              <select value={form.bay_id} onChange={(e) => setForm({ ...form, bay_id: e.target.value })} className={inp} data-testid="appt-bay">
                <option value="">— No bay —</option>
                {baysList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">EST. PRICE</div>
              <input type="number" value={form.estimated_price} onChange={(e) => setForm({ ...form, estimated_price: e.target.value })} className={inp} data-testid="appt-price" />
              {promoQuote && promoQuote.total !== promoQuote.regular && (
                <button type="button" data-testid="appt-apply-promo"
                  onClick={() => {
                    setForm((f) => ({ ...f, estimated_price: promoQuote.total }));
                    toast.success(`${promo.badge || "Promo"} applied: $${promoQuote.regular} → $${promoQuote.total}`);
                  }}
                  className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] hover:bg-emerald-500/20 transition-colors">
                  {promo?.badge || "Grand Opening"}{vclass ? ` · ${vclass}` : ""}: <s className="opacity-60">${promoQuote.regular}</s> → ${promoQuote.total} — apply
                </button>
              )}
            </label>
          </div>
          {isEdit && appt.status === "booked" && (
            <div>
              <div className="text-[11px] font-mono-alt text-white/50 mb-2 tracking-widest">BOOKING DECISION</div>
              {askDecision ? (
                <div className={`rounded-lg border p-3 ${askDecision === "confirmed" ? "bg-emerald-500/10 border-emerald-500/30" : "bg-red-500/10 border-red-500/30"}`}
                  data-testid="appt-decision-box">
                  <p className="text-sm text-white/80 mb-3">
                    {askDecision === "confirmed"
                      ? "Confirm this appointment? The customer will get a confirmation email with the date and studio address."
                      : "Decline this appointment? The customer will get a polite cancellation email."}
                  </p>
                  <div className="flex gap-2">
                    <button onClick={decide} disabled={busy}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold ${askDecision === "confirmed" ? "bg-emerald-500 text-black hover:bg-emerald-400" : "bg-red-500 text-white hover:bg-red-400"} disabled:opacity-40`}
                      data-testid="appt-decision-yes">
                      {busy ? "…" : askDecision === "confirmed" ? "Yes, confirm" : "Yes, decline"}
                    </button>
                    <button onClick={() => setAskDecision(null)} className="px-4 py-2 text-sm text-white/50 hover:text-white"
                      data-testid="appt-decision-back">
                      Back
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setAskDecision("confirmed")}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 text-sm font-semibold"
                    data-testid="appt-confirm-btn">
                    Confirm appointment
                  </button>
                  <button onClick={() => setAskDecision("cancelled")}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 text-sm font-semibold"
                    data-testid="appt-decline-btn">
                    Decline
                  </button>
                </div>
              )}
            </div>
          )}


          <label className="block">
            <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">SERVICES</div>
            <div className="max-h-40 overflow-y-auto p-2 bg-white/3 border border-white/10 rounded-lg space-y-1">
              {services.map((s) => (
                <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer">
                  <input type="checkbox" checked={form.service_ids.includes(s.id)}
                    onChange={(e) => setForm({ ...form, service_ids: e.target.checked ? [...form.service_ids, s.id] : form.service_ids.filter((x) => x !== s.id) })}
                    className="accent-[#1F5EFF]" data-testid={`appt-svc-${s.id}`} />
                  <span className="text-sm text-white/70 flex-1">{s.name}</span>
                  <span className="text-xs text-white/40">${s.price}</span>
                </label>
              ))}
            </div>
          </label>
          <label className="block">
            <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">NOTES</div>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inp + " resize-y"} data-testid="appt-notes" />
          </label>

          {!isEdit && selCustomer?.email && (
            <label className="flex items-center gap-2.5 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer" data-testid="appt-send-confirmation-wrap">
              <input type="checkbox" checked={sendConfirm} onChange={(e) => setSendConfirm(e.target.checked)}
                className="accent-[#1F5EFF] w-4 h-4" data-testid="appt-send-confirmation" />
              <span className="text-sm text-white/80">Email booking confirmation to <span className="text-white">{selCustomer.email}</span></span>
            </label>
          )}

          {isEdit && (
            <div>
              <div className="text-[11px] font-mono-alt text-white/50 mb-2 tracking-widest">STATUS</div>
              <div className="flex flex-wrap gap-1.5">
                {STATUS_LIST.map((s) => (
                  <button key={s} onClick={() => setStatus(s)} data-testid={`appt-status-${s}`}
                    className={`px-2.5 py-1 rounded text-xs capitalize ${appt.status === s ? STATUS_COLORS[s] : "bg-white/5 text-white/50 hover:bg-white/10"}`}>
                    {s.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-white/10 flex items-center gap-2">
          {isEdit && <button onClick={remove} className="text-red-400 hover:text-red-300 text-sm" data-testid="appt-delete">Delete</button>}
          {isEdit && onCheckIn && appt?.customer_id && !["completed", "cancelled", "no_show"].includes(appt.status) && (
            <button onClick={onCheckIn}
              className="px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 text-sm font-medium flex items-center gap-1.5"
              data-testid="appt-checkin">
              <ClipboardCheck className="w-4 h-4" /> Check-In
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 text-white/50 hover:text-white text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-6 py-2 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium disabled:opacity-40" data-testid="appt-save">
            {busy ? "…" : isEdit ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
